import React, { useEffect, useState } from "react";

import { useHistory, useLocation } from "react-router-dom";

import {
  FiActivity,
  FiBriefcase,
  FiGrid,
  FiHelpCircle,
  FiLogOut,
  FiMonitor,
  FiPackage,
  FiSettings,
  FiShield,
  FiShoppingBag,
  FiUsers,
  FiX,
} from "react-icons/fi";

import { FaThumbtack } from "react-icons/fa";

import usePlatformAuth from "../../../../../hooks/usePlatformAuth";

import "./SuperAdminNavbar.css";

const DESKTOP_BREAKPOINT = 992;

const PLATFORM_MENU = [
  {
    key: "dashboard",
    label: "Dashboard",
    path: "/platform/dashboard",
    icon: FiGrid,
  },
  {
    key: "businesses",
    label: "Businesses",
    path: "/platform/businesses",
    icon: FiBriefcase,
  },
  {
    key: "tenant_lead",
    label: "Business Partner Leads",
    path: "/platform/business-partner-leads",
    icon: FiUsers,
  },
  {
    key: "sale_leads",
    label: "Sell Device Leads",
    path: "/platform/sell-device-leads",
    icon: FiUsers,
  },

  {
    key: "tenant_users",
    label: "Tenant Users",
    path: "/platform/tenant-users",
    icon: FiUsers,
  },
  {
    key: "website_users",
    label: "Website Users",
    path: "/platform/website-users",
    icon: FiUsers,
  },
  {
    key: "subscription-plans",
    label: "Subscription Plans",
    path: "/platform/subscription-plans",
    icon: FiPackage,
  },
  {
    key: "subscriptions",
    label: "Subscriptions",
    path: "/platform/subscriptions",
    icon: FiShoppingBag,
  },
  {
    key: "support",
    label: "Support",
    path: "/platform/support",
    icon: FiHelpCircle,
  },
];

const PLATFORM_SYSTEM_MENU = [
  {
    key: "activity",
    label: "Platform Activity",
    path: "/platform/activity",
    icon: FiActivity,
  },
  {
    key: "client-app-settings",
    label: "Client App Settings",
    path: "/platform/client-app-setting",
    icon: FiSettings,
  },
  {
    key: "settings",
    label: "Settings",
    path: "/platform/settings",
    icon: FiSettings,
  },
];

const SuperAdminNavItem = ({ item, active, showText, onClick }) => {
  const Icon = item.icon;

  const className = [
    "super-admin-nav-link",
    active ? "active" : "",
    item.disabled ? "disabled" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      disabled={item.disabled}
      title={
        !showText
          ? item.label
          : item.disabled
            ? `${item.label} - Coming Soon`
            : item.label
      }
    >
      <span className="super-admin-nav-icon">{Icon ? <Icon /> : null}</span>

      {showText ? (
        <>
          <span className="super-admin-nav-label">{item.label}</span>

          {item.disabled ? (
            <small className="super-admin-nav-soon">Soon</small>
          ) : null}
        </>
      ) : null}
    </button>
  );
};

const SuperAdminNavbar = ({
  pinnedOpen = false,
  onPinnedOpenChange,
  mobileOpen = false,
  onMobileOpenChange,
}) => {
  const history = useHistory();

  const location = useLocation();

  const { platformUser, platformLogout } = usePlatformAuth();

  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`).matches
      : true,
  );

  const [hoverExpanded, setHoverExpanded] = useState(false);

  const desktopExpanded = isDesktop && (pinnedOpen || hoverExpanded);

  const showText = !isDesktop || desktopExpanded;

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const media = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);

    const handleChange = (event) => {
      setIsDesktop(event.matches);

      if (event.matches) {
        onMobileOpenChange?.(false);
      } else {
        setHoverExpanded(false);
      }
    };

    setIsDesktop(media.matches);

    if (media.addEventListener) {
      media.addEventListener("change", handleChange);
    } else {
      media.addListener(handleChange);
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener("change", handleChange);
      } else {
        media.removeListener(handleChange);
      }
    };
  }, [onMobileOpenChange]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    if (!isDesktop && mobileOpen) {
      document.body.classList.add("super-admin-sidebar-open");
    } else {
      document.body.classList.remove("super-admin-sidebar-open");
    }

    return () => {
      document.body.classList.remove("super-admin-sidebar-open");
    };
  }, [isDesktop, mobileOpen]);

  const isActivePath = (path) => {
    const currentPath = location.pathname;

    if (path === "/platform/dashboard") {
      return currentPath === path;
    }

    return currentPath === path || currentPath.startsWith(`${path}/`);
  };

  const closeMobile = () => {
    if (!isDesktop) {
      onMobileOpenChange?.(false);
    }
  };

  const handleNavigation = (item) => {
    if (item?.disabled || !item?.path) {
      return;
    }

    closeMobile();

    if (isDesktop && !pinnedOpen) {
      setHoverExpanded(false);
    }

    if (location.pathname === item.path) {
      return;
    }

    history.push(item.path);
  };

  const handleLogout = async () => {
    try {
      if (typeof platformLogout === "function") {
        await platformLogout();
      }
    } catch (error) {
      console.error("Platform logout error:", error);
    } finally {
      history.replace("/platform/login");
    }
  };

  const userInitial = String(platformUser?.name || "P")
    .trim()
    .charAt(0)
    .toUpperCase();

  const userName = platformUser?.name || "Platform Admin";

  const roleLabel =
    String(platformUser?.role || "")
      .trim()
      .toUpperCase() === "PLATFORM_SUPER_ADMIN"
      ? "Platform Super Admin"
      : platformUser?.role || "Platform Admin";

  const sidebarClassName = [
    "super-admin-sidebar",
    isDesktop ? "desktop-mode" : "mobile-mode",
    desktopExpanded ? "expanded" : "collapsed",
    pinnedOpen ? "pinned" : "",
    !isDesktop && mobileOpen ? "mobile-open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      {!isDesktop && mobileOpen ? (
        <div className="super-admin-sidebar-overlay" onClick={closeMobile} />
      ) : null}

      <aside
        className={sidebarClassName}
        onMouseEnter={() => {
          if (isDesktop && !pinnedOpen) {
            setHoverExpanded(true);
          }
        }}
        onMouseLeave={() => {
          if (isDesktop && !pinnedOpen) {
            setHoverExpanded(false);
          }
        }}
      >
        <div className="super-admin-sidebar-brand">
          <div className="super-admin-sidebar-logo">
            <FiMonitor />
          </div>

          {showText ? (
            <div className="super-admin-sidebar-brand-copy">
              <strong>COMKWIK</strong>

              <span>Platform Control</span>
            </div>
          ) : null}

          {!isDesktop ? (
            <button
              type="button"
              className="super-admin-sidebar-action"
              onClick={closeMobile}
              title="Close menu"
            >
              <FiX />
            </button>
          ) : null}

          {isDesktop && desktopExpanded ? (
            <button
              type="button"
              className={
                pinnedOpen
                  ? "super-admin-sidebar-action pin active"
                  : "super-admin-sidebar-action pin"
              }
              onClick={() => {
                const nextPinnedState = !pinnedOpen;

                onPinnedOpenChange?.(nextPinnedState);

                /*
                 * Default state is pinned/open.
                 *
                 * When the user clicks the pin while it is pinned,
                 * switch to AUTO mode:
                 *
                 * - sidebar collapses automatically
                 * - expands on hover
                 * - collapses again on mouse leave
                 */
                setHoverExpanded(false);
              }}
              title={
                pinnedOpen ? "Switch to auto collapse" : "Keep sidebar open"
              }
            >
              <FaThumbtack />
            </button>
          ) : null}
        </div>

        <div
          className={
            showText
              ? "super-admin-user-card"
              : "super-admin-user-card collapsed"
          }
        >
          <div className="super-admin-user-avatar">{userInitial}</div>

          {showText ? (
            <>
              <div className="super-admin-user-copy">
                <strong>{userName}</strong>

                <span>{roleLabel}</span>
              </div>

              <FiShield className="super-admin-user-shield" />
            </>
          ) : null}
        </div>

        <nav className="super-admin-sidebar-nav">
          {showText ? (
            <span className="super-admin-nav-section-title">PLATFORM</span>
          ) : null}

          <div className="super-admin-nav-list">
            {PLATFORM_MENU.map((item) => (
              <SuperAdminNavItem
                key={item.key}
                item={item}
                active={isActivePath(item.path)}
                showText={showText}
                onClick={() => handleNavigation(item)}
              />
            ))}
          </div>

          {showText ? (
            <span className="super-admin-nav-section-title super-admin-nav-section-spacing">
              SYSTEM
            </span>
          ) : (
            <div className="super-admin-nav-collapsed-divider" />
          )}

          <div className="super-admin-nav-list">
            {PLATFORM_SYSTEM_MENU.map((item) => (
              <SuperAdminNavItem
                key={item.key}
                item={item}
                active={isActivePath(item.path)}
                showText={showText}
                onClick={() => handleNavigation(item)}
              />
            ))}
          </div>
        </nav>

        <div className="super-admin-sidebar-bottom">
          {showText ? (
            <div className="super-admin-system-status">
              <span className="super-admin-system-dot" />

              <div>
                <strong>Platform Online</strong>

                <span>All services operational</span>
              </div>
            </div>
          ) : (
            <div
              className="super-admin-system-dot-only"
              title="Platform Online"
            >
              <span className="super-admin-system-dot" />
            </div>
          )}

          <button
            type="button"
            className={
              showText
                ? "super-admin-logout-button"
                : "super-admin-logout-button collapsed"
            }
            onClick={handleLogout}
            title="Sign Out"
          >
            <FiLogOut />

            {showText ? <span>Sign Out</span> : null}
          </button>
        </div>
      </aside>
    </>
  );
};

export default SuperAdminNavbar;
